import { PlayerStatus, type Room } from '@infiltre/shared';
import { motion } from 'motion/react';
import { useT } from '@/shared/lib/i18n';
import { mugshotForPlayer } from '@/shared/lib/mugshot';
import { Button, MugshotPortrait, PaperSheet, PhaseHeader, Pin, Stamp } from '@/shared/ui';

export type NoVerdictScreenProps = {
  room: Room;
  onContinue: () => void;
  continueLabel: string;
};

/**
 * Round "no verdict" — votes tied or split with no eliminate. Mirrors
 * `VerdictScreen` language (pinned dossier, hard-edge stamp, polaroid
 * cast strip) so tie rounds don't fall back to a bare bulleted list.
 */
export const NoVerdictScreen = ({ room, onContinue, continueLabel }: NoVerdictScreenProps) => {
  const t = useT();
  const stillIn = room.players.filter((p) => p.status !== PlayerStatus.Eliminated);

  return (
    <main className="mx-auto flex h-full w-full max-w-md flex-col">
      <PhaseHeader
        eyebrow={t('round.verdict.phase_eyebrow')}
        title={t('round.verdict.no_elimination.title')}
        descriptor={t('round.verdict.no_elimination.descriptor')}
      />

      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-5 py-6">
        <motion.div
          initial={{ opacity: 0, y: 10, rotate: 0, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, rotate: -1.5, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.18, 1.15, 0.4, 1] }}
          className="relative w-[300px]"
        >
          <Pin left="28%" top={-8} size={14} />
          <Pin left="72%" top={-8} size={14} />
          <PaperSheet
            tone="cream"
            seed={`no-verdict-${room.round}`}
            className="px-5 pt-6 pb-5"
            style={{ boxShadow: 'var(--shadow-paper-deep)' }}
          >
            <div className="flex items-center justify-center py-4">
              <Stamp
                tone="red"
                rotate={-6}
                animate
                className="px-5 py-2 text-[20px] tracking-[0.14em]"
              >
                {t('round.verdict.no_elimination.stamp')}
              </Stamp>
            </div>
            <p className="mt-2 text-center text-[12px] text-ink/65 italic leading-snug">
              {t('round.verdict.no_elimination.descriptor')}
            </p>
          </PaperSheet>
        </motion.div>

        <div className="w-full">
          <div className="eyebrow text-center text-[10px] text-cream/55 tracking-[0.22em]">
            {t('round.verdict.no_elimination.still_in')}
          </div>
          <ul className="mt-3 flex flex-wrap justify-center gap-3">
            {stillIn.map((p, i) => {
              const tilt = [-3, 2, -2, 3, -4, 4][i % 6]!;
              return (
                <motion.li
                  key={p.id}
                  initial={{ opacity: 0, scale: 0.8, rotate: 0 }}
                  animate={{ opacity: 1, scale: 1, rotate: tilt }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.32 }}
                  className="w-[78px]"
                >
                  <PaperSheet
                    tone="cream"
                    seed={p.id}
                    className="px-1 pt-1 pb-1.5"
                    style={{ boxShadow: 'var(--shadow-paper-soft)' }}
                  >
                    <div className="relative flex h-[58px] w-full items-center justify-center overflow-hidden border border-ink/15">
                      <MugshotPortrait config={mugshotForPlayer(p)} />
                    </div>
                    <div className="mt-0.5 truncate text-center font-hand text-[13px] text-ink leading-tight">
                      {p.name}
                    </div>
                  </PaperSheet>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="px-5 pt-2 pb-5">
        <Button variant="orange" onClick={onContinue}>
          {continueLabel}
        </Button>
      </div>
    </main>
  );
};
